import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Languages, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const LANGS = [
  { k: "en", label: "English", required: true },
  { k: "hi", label: "हिन्दी" },
  { k: "kn", label: "ಕನ್ನಡ" },
  { k: "ta", label: "தமிழ்" },
  { k: "ml", label: "മലയാളം" },
];
const MODULES = ["ui", "dashboard", "job_cards", "invoices", "email", "sms", "pdf", "landing"];
const EMPTY = { key: "", module: "ui", en: "", hi: "", kn: "", ta: "", ml: "", notes: "" };

export default function AdminTranslations() {
  const { user: me } = useAuth();
  const canEdit = ["platform_owner", "translation_admin"].includes(me?.admin_role);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [moduleF, setModuleF] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (moduleF) params.module = moduleF;
    if (missingOnly) params.missing_only = true;
    api.get("/admin/master-data/translations", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [moduleF, missingOnly]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      key: t.key, module: t.module || "ui",
      en: t.en || "", hi: t.hi || "", kn: t.kn || "", ta: t.ta || "", ml: t.ml || "",
      notes: t.notes || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.key.trim() || !form.en.trim()) { toast.error("Key and English are required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/master-data/translations/${editing.id}`, form);
        toast.success("Translation saved");
      } else {
        await api.post("/admin/master-data/translations", form);
        toast.success("Translation created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.key}"?`)) return;
    try {
      await api.delete(`/admin/master-data/translations/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const missingCount = (t) => LANGS.slice(1).filter((l) => !t[l.k]).length;

  return (
    <div className="space-y-5" data-testid="admin-translations-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Master Data</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <Languages size={22} className="text-[#457b9d]" /> Translations
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            {items.length} translation key(s) across 5 languages. Curated by the Translation Admin team.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            data-testid="admin-translations-new"
            className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
          >
            <Plus size={14} className="mr-1.5" /> New key
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={moduleF}
          onChange={(e) => setModuleF(e.target.value)}
          data-testid="admin-translations-module-filter"
          className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
        >
          <option value="">All modules</option>
          {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
          <input
            type="checkbox"
            checked={missingOnly}
            onChange={(e) => setMissingOnly(e.target.checked)}
            data-testid="admin-translations-missing-only"
            className="accent-[#e63946]"
          />
          Show only rows with missing languages
        </label>
        <Input
          data-testid="admin-translations-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search key or English text…"
          className="flex-1 min-w-[220px] max-w-md h-9 rounded-md text-[13px]"
        />
      </div>

      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-translations-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Key</th>
              <th className="text-left px-4 py-3 font-semibold">Module</th>
              <th className="text-left px-4 py-3 font-semibold">English</th>
              <th className="text-left px-4 py-3 font-semibold">Coverage</th>
              {canEdit && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">
                No translations yet. Click &quot;New key&quot; to add your first phrase.
              </td></tr>
            )}
            {items.map((t) => {
              const missing = missingCount(t);
              return (
                <tr key={t.id} data-testid={`admin-translation-row-${t.id}`} className="hover:bg-[#F7F9FC]">
                  <td className="px-4 py-3 font-mono text-[12px] text-[#1d3557] font-semibold">{t.key}</td>
                  <td className="px-4 py-3 text-[11px] uppercase tracking-wider text-[#457b9d] font-semibold">{t.module}</td>
                  <td className="px-4 py-3 text-[13px] text-[#1d3557] max-w-md truncate" title={t.en}>{t.en}</td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1">
                      {LANGS.slice(1).map((l) => (
                        <span
                          key={l.k}
                          title={`${l.label}: ${t[l.k] || "missing"}`}
                          className={`w-6 h-5 grid place-items-center rounded text-[9px] font-bold uppercase tracking-wider ${
                            t[l.k]
                              ? "bg-[#12B76A]/10 text-[#12B76A]"
                              : "bg-[#F79009]/10 text-[#F79009]"
                          }`}
                        >
                          {l.k}
                        </span>
                      ))}
                      {missing > 0 && (
                        <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold text-[#F79009]">
                          <AlertTriangle size={10} /> {missing} missing
                        </span>
                      )}
                    </div>
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-3">
                        <button
                          onClick={() => openEdit(t)}
                          data-testid={`admin-translation-edit-${t.id}`}
                          className="text-[12px] text-[#457b9d] hover:underline font-semibold inline-flex items-center gap-1"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                        <button
                          onClick={() => remove(t)}
                          data-testid={`admin-translation-delete-${t.id}`}
                          className="text-[12px] text-[#e63946] hover:underline font-semibold inline-flex items-center gap-1"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg border-[#E4E7EC] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1d3557] font-semibold">
              {editing ? `Edit "${editing.key}"` : "New translation key"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Key</Label>
                <Input
                  data-testid="admin-translation-key"
                  value={form.key}
                  onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                  className="mt-1 rounded-md font-mono"
                  placeholder="e.g. dashboard.welcome"
                  disabled={!!editing}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Module</Label>
                <select
                  data-testid="admin-translation-module"
                  value={form.module}
                  onChange={(e) => setForm((f) => ({ ...f, module: e.target.value }))}
                  className="mt-1 w-full h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] text-[#1d3557]"
                >
                  {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            {LANGS.map((l) => (
              <div key={l.k}>
                <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">
                  {l.label}{l.required ? " *" : ""}
                </Label>
                <Textarea
                  data-testid={`admin-translation-${l.k}`}
                  rows={l.k === "en" ? 2 : 1}
                  value={form[l.k]}
                  onChange={(e) => setForm((f) => ({ ...f, [l.k]: e.target.value }))}
                  className="mt-1 rounded-md"
                />
              </div>
            ))}
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Notes (for translators)</Label>
              <Textarea
                data-testid="admin-translation-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="Context, tone, glossary references…"
              />
            </div>
            <Button
              onClick={save}
              data-testid="admin-translation-save"
              className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
            >
              {editing ? "Save changes" : "Create key"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
